import { useCookieConsent } from '@/hooks/useCookieConsent';
import { cookieCopyEN } from '@/lib/cookieCopy';
// icons not needed after removing bullets
import { useEffect, useState } from 'react';

export default function CookieBanner() {
  const { shouldShowBanner, config, acceptAll, rejectNonEssential } = useCookieConsent();
  const [hiddenForManage, setHiddenForManage] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [render, setRender] = useState(false);

  useEffect(() => {
    const onClosed = () => setHiddenForManage(false);
    const onOpen = () => setHiddenForManage(true);
    window.addEventListener('cookie-settings-closed', onClosed);
    window.addEventListener('open-cookie-settings', onOpen as any);
    return () => {
      window.removeEventListener('cookie-settings-closed', onClosed);
      window.removeEventListener('open-cookie-settings', onOpen as any);
    };
  }, []);

  // No persistent dismissal: each refresh will re-evaluate server consent status

  // compute visibility intent
  const show = shouldShowBanner && !hiddenForManage && !dismissed;

  // mount/unmount with exit animation
  useEffect(() => {
    if (show) {
      setExiting(false);
      setRender(true);
    } else if (render) {
      setExiting(true);
      const t = setTimeout(() => {
        setRender(false);
        setExiting(false);
      }, 260);
      return () => clearTimeout(t);
    }
  }, [show]);

  if (!render) return null;

  const title = config?.title || cookieCopyEN.banner.title;
  const desc = config?.description || cookieCopyEN.banner.description;
  const privacyUrl = config?.links?.privacyPolicyUrl || '/privacy';

  return (
  <div className={`fixed inset-x-0 bottom-0 z-[120] ${exiting ? 'animate-[slideDown_240ms_ease-in]' : 'animate-[slideUp_300ms_ease-out]'}`}>
      <div className="mx-auto max-w-4xl">
        <div
          role="dialog"
          aria-labelledby="cookie-banner-title"
          className="relative m-3 rounded-t-xl border border-gray-200 bg-white text-gray-900 shadow-2xl"
        >
          {/* Close: dismiss for this session only (shows again on refresh) */}
          <button
            type="button"
            aria-label="Dismiss cookie banner"
            onClick={() => {
              // Hide banner temporarily until next refresh (no consent decision)
              setDismissed(true);
            }}
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            ×
          </button>

          <div className="p-4 sm:p-5 pr-12">
            <h2 id="cookie-banner-title" className="text-base sm:text-lg font-semibold">{title}</h2>
            <div className="mt-2 space-y-2 text-xs sm:text-sm leading-relaxed text-gray-700">
              <p>{desc}</p>
              <p>
                You can manage which cookies are set on your device anytime from “Cookie Settings” in the footer.
              </p>
              <p>
                For more information see our{' '}
                <a href={privacyUrl} className="underline text-teal-700 hover:text-teal-600">privacy policy</a>.
              </p>
            </div>

            {/* Actions */}
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
              <button
                onClick={() => {
                  // Hide instantly, then persist choice
                  setDismissed(true);
                  rejectNonEssential();
                }}
                className="order-3 sm:order-1 text-xs sm:text-sm text-gray-600 underline underline-offset-2 hover:text-gray-800 self-start sm:self-auto"
              >
                {config?.actions?.reject_non_essential || cookieCopyEN.banner.buttons.reject}
              </button>

              <button
                onClick={() => {
                  setHiddenForManage(true);
                  window.dispatchEvent(new Event('open-cookie-settings'));
                }}
                className="order-1 sm:order-2 inline-flex justify-center rounded-md border border-gray-300 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-800 hover:bg-gray-100"
              >
                {config?.actions?.manage_settings || 'Manage Cookies'}
              </button>
              <button
                onClick={() => {
                  // Hide instantly, then persist choice
                  setDismissed(true);
                  acceptAll();
                }}
                className="order-2 sm:order-3 inline-flex justify-center rounded-md bg-teal-700 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-white hover:bg-teal-600"
              >
                {config?.actions?.accept_all || 'Accept Cookies'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Inline keyframes for slide animations (enter: up, exit: down)
;(function addSlideKeyframesOnce(){
  if (typeof document === 'undefined') return;
  const id = 'cookie-consent-anim';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.innerHTML = `@keyframes slideUp{from{transform:translateY(100%);opacity:.0}to{transform:translateY(0);opacity:1}}
@keyframes slideDown{from{transform:translateY(0);opacity:1}to{transform:translateY(100%);opacity:.0}}`;
  document.head.appendChild(style);
})();
